import { useState, useEffect, useCallback } from 'react';
import { Table, TableHeader, TableBody, Column, Row } from 'react-aria-components';
import { useLocation } from 'react-router-dom';

import { useAtom } from 'jotai';

import { getOffices } from '@/api/getOffices';
import Breadcrumbs, { BreadcrumbItem } from '@/components/Breadcrumbs/Breadcrumbs';
import Button from '@/components/Button/Button';
import { CreateOrEditOfficeDialog } from '@/components/offices/CreateOrEditOfficeDialog';
import { PREFECTURE_OPTIONS } from '@/const/prefecture';
import { NotificationAtom, UserAtom } from '@/jotai/jotai';
import { Office } from '@/types/office';

export const OfficesPage = () => {
  const email = localStorage.getItem('email') || '';
  const [offices, setOffices] = useState<Office[]>([]);
  const [selectedOffice, setSelectedOffice] = useState<Office | undefined>(undefined);
  const [isOpenCreateDialog, setIsOpenCreateDialog] = useState(false);
  const [isEditOpenDialog, setIsEditOpenDialog] = useState(false);

  const [_, setNotification] = useAtom(NotificationAtom);
  const [loginUser] = useAtom(UserAtom);

  const { pathname: pathName } = useLocation();
  const breadcrumbs: BreadcrumbItem[] = [{ path: pathName, name: '事業所マスタ' }];

  const fetchOffices = useCallback(async () => {
    try {
      const result = await getOffices(email);
      if (result.status === 200) {
        setOffices(result.result);
      } else {
        setNotification({
          headingLevel: 'h2',
          type: 'error',
          bannerStyle: 'standard',
          title: '事業所の取得に失敗しました',
          message: '事業所の取得に失敗しました',
        });
      }
    } catch (error) {
      setNotification({
        headingLevel: 'h2',
        type: 'error',
        bannerStyle: 'standard',
        title: '事業所の取得に失敗しました',
        message: '事業所の取得に失敗しました',
      });
    }
  }, []);

  useEffect(() => {
    fetchOffices();
  }, []);

  return (
    <div className="flex flex-col">
      <div className="flex flex-row">
        <Breadcrumbs item={breadcrumbs} />
      </div>
      <div className="mb-4 mt-5 flex flex-col items-start justify-between gap-5">
        <h1 className="text-2xl font-bold">事業所マスタ</h1>
        <div className="flex-shrink-0">
          {loginUser?.is_super_admin && (
            <Button
              type="submit"
              size="lg"
              variant={'solid-fill'}
              onClick={() => setIsOpenCreateDialog(true)}
            >
              新規登録
            </Button>
          )}
        </div>
      </div>
      {isOpenCreateDialog && (
        <CreateOrEditOfficeDialog
          isOpen={isOpenCreateDialog}
          setIsOpen={setIsOpenCreateDialog}
          afterSubmit={() => {
            fetchOffices();
          }}
        />
      )}

      <Table className="w-full border-collapse border border-gray-200">
        <TableHeader>
          <Column
            isRowHeader
            className="border border-gray-200 bg-gray-50 px-4 py-2 font-medium"
          >
            No
          </Column>
          <Column
            isRowHeader
            className="border border-gray-200 bg-gray-50 px-4 py-2 font-medium"
          >
            事業者番号
          </Column>
          <Column className="border border-gray-200 bg-gray-50 px-4 py-2 font-medium">
            事業者名
          </Column>
          <Column className="border border-gray-200 bg-gray-50 px-4 py-2 font-medium">
            代表者名
          </Column>
          <Column className="border border-gray-200 bg-gray-50 px-4 py-2 font-medium">
            郵便番号
          </Column>
          <Column className="border border-gray-200 bg-gray-50 px-4 py-2 font-medium">
            都道府県
          </Column>
          <Column className="border border-gray-200 bg-gray-50 px-4 py-2 font-medium">
            市区町村
          </Column>
          <Column className="border border-gray-200 bg-gray-50 px-4 py-2 font-medium">住所</Column>
          <Column className="border border-gray-200 bg-gray-50 px-4 py-2 font-medium">
            事業所電話番号
          </Column>
          <Column className="border border-gray-200 bg-gray-50 px-4 py-2 font-medium">編集</Column>
        </TableHeader>
        <TableBody>
          {offices.map((office, index) => (
            <Row key={index}>
              <Column
                key="no"
                className="border border-gray-200 px-4 py-2 font-medium"
              >
                {office.id}
              </Column>
              <Column
                key="name"
                className="border border-gray-200 px-4 py-2 font-medium"
              >
                {office.officeNumber}
              </Column>
              <Column
                key="officeNumber"
                className="border border-gray-200 px-4 py-2 font-medium"
              >
                {office.name}
              </Column>
              <Column
                key="representative"
                className="border border-gray-200 px-4 py-2 font-medium"
              >
                {office.representative}
              </Column>
              <Column
                key="postalCode"
                className="border border-gray-200 px-4 py-2 font-medium"
              >
                {office.postalCode}
              </Column>
              <Column
                key="prefectureId"
                className="border border-gray-200 px-4 py-2 font-medium"
              >
                {PREFECTURE_OPTIONS.find((pref) => Number(pref.id) === office.prefectureId)?.name ||
                  '不明'}
              </Column>
              <Column
                key="municipality"
                className="border border-gray-200 px-4 py-2 font-medium"
              >
                {office.municipality}
              </Column>
              <Column
                key="address"
                className="border border-gray-200 px-4 py-2 font-medium"
              >
                {office.address1} {office.address2}
              </Column>
              <Column
                key="phoneNumber"
                className="border border-gray-200 px-4 py-2 font-medium"
              >
                {office.phoneNumber}
              </Column>

              <Column
                key="edit"
                className="border border-gray-200 px-4 py-2"
              >
                <Button
                  size="sm"
                  variant="text"
                  type="submit"
                  onClick={() => {
                    setIsEditOpenDialog(true);
                    setSelectedOffice(office);
                  }}
                >
                  編集
                </Button>
              </Column>
            </Row>
          ))}
        </TableBody>
      </Table>
      {isEditOpenDialog && (
        <CreateOrEditOfficeDialog
          isOpen={isEditOpenDialog}
          setIsOpen={setIsEditOpenDialog}
          afterSubmit={() => {
            fetchOffices();
          }}
          office={selectedOffice}
        />
      )}
    </div>
  );
};
